import {EventEmitter2, OnOptions} from "eventemitter2";

/**
 * @namespace Salla.event
 *
 * @property {string} delimiter
 * @property {array} loggableEvents - log errors or important events.
 */
export default class Emitter extends EventEmitter2 {
  delimiter: string;
  body?: HTMLBodyElement;
  logableEvents: string[];
  emittedEvents: Set<string>;
  ingoreLogEvents: string[];
  noneFireableActions: string[];
  constructor() {
    let delimiter = "::";
    super({
      wildcard: true,
      delimiter: delimiter,
      newListener: false,
      removeListener: false,
      maxListeners: 10,
      verboseMemoryLeak: false,
      ignoreErrors: false,
    });
    this.delimiter = delimiter;

    //todo:: why we need this?
    if (typeof document !== "undefined") {
      this.body = document.querySelector("body");
    }

    this.logableEvents = ["cart::item.added.failed", "cart::item.deleted.failed"];

    this.ingoreLogEvents = [
      "document::click",
      "document::keyup",
      "document::change",
      //'document::submit',
    ];

    this.noneFireableActions = ["document.request"];
    this.emittedEvents = new Set();

  }

  /**
   * @param {string} event_name
   * @param data
   *
   * @deprecated , legacy code, use dispatch() instead
   */
  createAndDispatch(event_name, ...data) {
    this.dispatch(event_name, ...data);
  }

  //@ts-ignore
  emit(event_name, ...data) {
    this.emittedEvents.add(event_name);
    /**
     * if the event, is api/event method, call it,
     * ex: cart::quick.add call Salla.api.cart.quickAdd(...data)
     * we don't used Salla.call(actionName)(...data), to avoid losing this scope in our apis.
     *
     * @deprecated don't use it anymore
     */
    let actionName = event_name.replace("::", ".");
    if (!this.noneFireableActions.includes(actionName) && Salla.call && typeof Salla.call(actionName) === "function") {
      Salla.log(`'Salla.${actionName}(...)' triggered using event '${event_name}'`);
      actionName = actionName.split("."); //['cart','addItem']
      //when we are passing event from back end using `twilight()->addEvent('eventName', param1, param2, param3)`, here we will pass them back as variables
      if (Array.isArray(data[0])) {
        data = data[0];
      }
      salla[actionName[0]][actionName[1]](...data);
      return;
    }


    super.emit(event_name, ...data);
    this.trackEvents(event_name, ...data);
  }

  /**
   * Executes a callback either immediately if an event has already been emitted, or after the event occurs.
   * This is useful for handling cases where you need to respond to an event that might have already occurred
   * or will occur in the future.
   * 
   * @param {string} event_name - The name of the event to check/wait for
   * @param {Function|null} callback - The callback function to execute
   * @returns {Promise<any>} A promise that resolves with the callback's return value
   * 
   * @example
   * // Wait for 'cart::item.added' event if it hasn't occurred yet
   * event.onlyWhen('cart::item.added', () => {
   *   // This will run either immediately if cart is loaded
   *   // or after cart loads if it hasn't loaded yet
   *   console.log('Cart is ready');
   * });
   * //or
   * event.onlyWhen('cart::item.added').then(() => {
   *   // This will run either immediately if cart is loaded
   * })
   */
  onlyWhen(event_name, callback): Promise<any> {
    callback = callback || function() {};
    return new Promise((resolve) => this.emittedEvents.has(event_name)
            ? resolve(callback())
            : this.once(event_name, () => resolve(callback()))
        );
  }

  emitAsync(event_name, ...data) {
    this.emittedEvents.add(event_name);
    //to make sure that data object will not be affected by track process
    let result = super.emitAsync(event_name, ...data);
    try {
      this.trackEvents(event_name, ...data);
    } catch (e) {
      Salla.logger.warn(`error on tracking event (${event_name})`, data, e);
    }
    return result;
  }

  trackEvents(event_name, ...data){
    // let's send the event to data layer
    if (typeof window !== "undefined") {
      try {
        window.dataLayer = window.dataLayer || [];
        /**
         * data is coming as an array like this:
         * [{"services": {"intercom": {"app_id": "***"}}}]
         * we will merge it into one object  like this:
         * {event: event_name, "services": {"intercom": {"app_id": "***"}}}
         */
        let dataLayerObject = {event: event_name};
        data.map(item => typeof item === 'object' && (dataLayerObject = {...dataLayerObject, ...item}));
        window.dataLayer.push(dataLayerObject);
      }catch(e){
        salla.logger.error(e.message);
      }
    }

    //if there is need to log errors, or events, lets do it here.
    if (Salla.logger && !this.ingoreLogEvents.includes(event_name)) {
      Salla.logger.event(event_name, ...data);
    }
    this.dispatchMobileEvent(event_name,  ...data);
  }
  /**
   * Fire Event with data
   *
   * @param {string} event_name - action name like `cart::addItem` or event name like `cart::item.added`
   * @param {any|undefined} data
   * @data {{payload}} data
   */
  dispatch(event_name, ...data) {
    return this.emit(event_name, ...data);
  }

  /**
   * Fire multi events using object of events
   *
   * @param {Object.<string, *>} events
   */
  dispatchEvents(events) {
    if (!events) {
      Salla.log("No Events To Dispatch!", events);
      return;
    }

  if(typeof events !== "object" || Array.isArray(events)){
    Salla.log("Events object is wrong, it should be object of {event:payload}", events);
    return;
  }

    for (const [event_name, payload] of Object.entries(events)) {
      this.dispatch(event_name, payload);
    }
  }

  /**
   * Alias for on function
   *
   * @param {string} eventName
   * @param {function} callback
   * @param options
   * @deprecated use on
   */
  addListener(eventName, callback, options?: boolean | OnOptions) {
    return this.on(eventName, callback, options);
  }

  /**
   * Alias for on function
   *
   * @param {string} eventName
   * @param {function} callback
   * @param {OnOptions} options
   * @deprecated use on
   */
  addEventListener(eventName, callback, options?: boolean | OnOptions) {
    return this.on(eventName, callback, options);
  }

  /**
   * Alias for this.addListener
   * @param {string} eventName
   * @param {function} callback
   */
  listen(eventName, callback) {
    return this.on(eventName, callback);
  }

  /**
   * Register Global listener will allow you to track all events in salla,
   * all Salla events will push to it.
   *
   * @param {string} name
   * @param {function} callback
   * @deprecated use onAny, it maybe used in the apps
   */
  registerGlobalListener(name, callback) {
    return this.onAny(callback);
  }

  /**
   * This help to push an event to mobile app.
   *
   * @param eventName
   * @param details
   * @returns {string}
   */
  dispatchMobileEvent(eventName, details = {}) {
    if (typeof window === "undefined") {
      return;
    }

    if (window.webkit) {
      try {
        window.webkit.messageHandlers.callbackHandler.postMessage(
          JSON.stringify({
            event: eventName,
            details: details,
          })
        );
        return;
      } catch (err) {
        Salla.log(err, "The native context does not exist yet");
      }
    }

    if (window.Android?.customEventWithData) {
      try {
        window.Android.customEventWithData(eventName, JSON.stringify({ details: details }));
      } catch (err) {
        Salla.log(err, "The native context does not exist yet");
      }
      return;
    }

    // Flutter support
    if (window.flutter_inappwebview) {
      try {
        window.flutter_inappwebview.callHandler('sallaEvent', {
          event: eventName,
          details: details,
        });
      } catch (err) {
        Salla.log(err, "The Flutter context does not exist yet");
      }
      return;
    }
  }
}
